import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export type TenantMode = 'all' | 'tenant' | 'assigned';
export type TenantUserRole = 'super_admin' | 'admin' | 'manager' | 'user';

export interface TenantListItem {
  id: string;
  name: string;
  isActive?: boolean;
  settings?: Record<string, unknown>;
}

interface HydrateTenantContextInput {
  role: TenantUserRole;
  assignedTenantId?: string | null;
  tenantList?: TenantListItem[];
  preferredTenantId?: string | null;
}

interface TenantState {
  mode: TenantMode;
  activeTenantId: string | null;
  tenantList: TenantListItem[];

  setMode: (mode: TenantMode) => void;
  setActiveTenantId: (tenantId: string | null) => void;
  setTenantList: (tenantList: TenantListItem[]) => void;
  hydrateTenantContext: (input: HydrateTenantContextInput) => void;
  clearInvalidSelection: () => void;
  resetTenantContext: () => void;
}

export const useTenantStore = create<TenantState>()(
  persist(
    set => ({
      mode: 'all',
      activeTenantId: null,
      tenantList: [],

      setMode: mode => {
        set(state => ({
          mode,
          activeTenantId: mode === 'all' ? null : state.activeTenantId,
        }));
      },

      setActiveTenantId: tenantId => {
        set(state => ({
          activeTenantId: tenantId,
          mode: tenantId ? (state.mode === 'assigned' ? 'assigned' : 'tenant') : 'all',
        }));
      },

      setTenantList: tenantList => {
        set({ tenantList });
      },

      hydrateTenantContext: ({
        role,
        assignedTenantId = null,
        tenantList,
        preferredTenantId = null,
      }) => {
        const isAdmin = role === 'admin' || role === 'super_admin';

        set(state => ({
          tenantList: tenantList ?? state.tenantList,
          mode: isAdmin ? (preferredTenantId ? 'tenant' : 'all') : 'assigned',
          activeTenantId: isAdmin ? preferredTenantId : assignedTenantId,
        }));
      },

      clearInvalidSelection: () => {
        set({
          mode: 'all',
          activeTenantId: null,
        });
      },

      resetTenantContext: () => {
        set({
          mode: 'all',
          activeTenantId: null,
          tenantList: [],
        });
      },
    }),
    {
      name: 'muraka-tenant',
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        mode: state.mode,
        activeTenantId: state.activeTenantId,
        tenantList: state.tenantList,
      }),
    }
  )
);
